import './table-retailer.scss';
import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import {useNavigate} from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import decodeToken from '../../../../utils/decode-token';
import {Alert, Snackbar, TableSortLabel} from '@mui/material';
import RowActionsMenu from '../../common/table/row-actions-menu';
import TableResultsSummary from '../../common/table/table-results-summary';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';

function TableRetailer({reload, onDataLoaded, showSummary = true, onSummaryChange = () => {}}) {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();

  const [retailers, setRetailers] = useState([]);
  const [filteredRetailers, setFilteredRetailers] = useState([]);

  const [countries, setCountries] = useState([]); // [{ ES: 'Spain' }, { PT: 'Portugal' }]
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState({
    name: '',
    country: '',
    city: '',
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Orden de la tabla
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (array) => {
    return array.sort((a, b) => {
      if (orderBy === 'name') {
        return order === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (orderBy === 'last_name') {
        return order === 'asc'
          ? (a.last_name || '').localeCompare(b.last_name || '')
          : (b.last_name || '').localeCompare(a.last_name || '');
      }
      if (orderBy === 'last_update') {
        return order === 'asc'
          ? new Date(a.last_update || 0) - new Date(b.last_update || 0)
          : new Date(b.last_update || 0) - new Date(a.last_update || 0);
      }
      return 0;
    });
  };

  const fetchData = async () => {
    const authToken = token;
    const url = `${process.env.REACT_APP_API_URL}/pma/retailers`;

    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(url, config);
      if (response.status === 200) {
        const data = response.data;

        const storedFilters = JSON.parse(sessionStorage.getItem('pma-retailer-filters') || '{}');
        const initialFilters = {
          ...filters,
          ...storedFilters,
        };

        setFilters(initialFilters);
        setRetailers(data);
        applyFilters(initialFilters, data);
        setDataLoaded(true);
        onDataLoaded();

        // Países únicos por area_code
        const availableCountryCodes = [
          ...new Set(data.map((retailer) => retailer.area_code))
        ];
        setCountries(orderCountries(availableCountryCodes));

        // Todas las ciudades disponibles
        const uniqueCities = [
          ...new Set(data.map((retailer) => retailer.city))
        ];
        setCities(uniqueCities);
      } else if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setRetailers, setFilteredRetailers, setCountries, setCities],
          onDataLoaded,
          onSummaryChange,
          setDataLoaded,
        });
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'), error);
      }
    }
  };

  useEffect(() => {
    if (reload) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [reload, token, onDataLoaded, t]);

  const handleFilterChange = (e) => {
    const {name, value} = e.target;

    if (name === "country") {
      if (value === "") {
        // País vacío → todas las ciudades de todos los retailers
        const allCities = [...new Set(retailers.map((retailer) => retailer.city))];
        setCities(allCities);

        setFilters({
          ...filters,
          country: "",
          city: "",
        });
      } else {
        // Filtrar ciudades solo para ese país
        const filteredCities = retailers
          .filter((retailer) => retailer.area_code === value)
          .map((retailer) => retailer.city);

        setCities([...new Set(filteredCities)]);

        setFilters({
          ...filters,
          country: value,
          city: "",
        });
      }
    } else if (name === "city") {
      setFilters({
        ...filters,
        city: value,
      });
    } else {
      setFilters({
        ...filters,
        [name]: value,
      });
    }
  };

  const applyFilters = (appliedFilters = filters, retailersData = retailers) => {
    let filtered = [...retailersData];

    if (appliedFilters.name) {
      filtered = filtered.filter((retailer) =>
        retailer.name.toLowerCase().includes(appliedFilters.name.toLowerCase())
      );
    }

    if (appliedFilters.country) {
      filtered = filtered.filter(
        (retailer) => retailer.area_code === appliedFilters.country
      );
    }

    if (appliedFilters.city) {
      filtered = filtered.filter((retailer) =>
        retailer.city.toLowerCase().includes(appliedFilters.city.toLowerCase())
      );
    }

    setFilteredRetailers(filtered);
    onSummaryChange({ total: filtered.length });
    sessionStorage.setItem('pma-retailer-filters', JSON.stringify(appliedFilters));
  };

  const handleApplyFilter = () => {
    applyFilters();
  };

  const resetFilters = () => {
    const initialFilters = {
      name: '',
      country: '',
      city: ''
    };

    const allCountries = [...new Set(retailers.map((retailer) => retailer.area_code))];
    const allCities = [...new Set(retailers.map((retailer) => retailer.city))];

    setCountries(orderCountries(allCountries));
    setCities(allCities);

    setFilters(initialFilters);
    sessionStorage.setItem('pma-retailer-filters', JSON.stringify(initialFilters));

    applyFilters(initialFilters, retailers);
  };

  // Recibe un array de códigos de país [ "ES", "PT" ]
  // y devuelve [{ ES: "Spain" }, { PT: "Portugal" }] ordenados por nombre
  const orderCountries = (countryCodes) => {
    return countryCodes
      .map(code => ({[code]: t(`countries-${code}`)}))
      .sort((a, b) => Object.values(a)[0].localeCompare(Object.values(b)[0]));
  };

  return (
    <>
      {dataLoaded ? (
        retailers && retailers.length !== 0 ? (
          <>
            <div className="filters">
              <p>{t('common-search')}</p>
              <Grid container spacing={3} rowSpacing={0}>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                  <div className="form-control">
                    <label htmlFor="name">{t('common-name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                    />
                  </div>
                </Grid>

                {/* Filtro de país basado en los area_code que llegan */}
                <Grid size={{xs: 12, sm: 6, md: 2}}>
                  <div className="form-control">
                    <label htmlFor="country">{t('common-country')}</label>
                    <select
                      name="country"
                      id="country"
                      className="normal"
                      value={filters.country}
                      onChange={handleFilterChange}
                    >
                      <option value="">{t('common-all-country')}</option>
                      {countries.map((country) => (
                        <option
                          key={Object.keys(country)[0]}
                          value={Object.keys(country)[0]}
                        >
                          {Object.values(country)[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                </Grid>

                <Grid size={{xs: 12, sm: 6, md: 2}}>
                  <div className="form-control">
                    <label htmlFor="city">{t('common-city')}</label>
                    <select
                      name="city"
                      id="city"
                      className="normal"
                      value={filters.city}
                      onChange={handleFilterChange}
                    >
                      <option value="">{t('common-all-city')}</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                </Grid>
              </Grid>

              {showSummary && (
                <TableResultsSummary count={filteredRetailers.length} />
              )}

              <Grid container spacing={3}>
                <Grid size={{xs: 12, sm: 6, md: 2}}>
                  <div className="actions">
                    <button className="btn" onClick={handleApplyFilter}>
                      {t('common-apply')}
                    </button>
                  </div>
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                  <div className="actions">
                    <button onClick={resetFilters} className="btn text narrow">
                      {t('common-reset-filters')}
                    </button>
                  </div>
                </Grid>
              </Grid>

            </div>

            <div className="table-wrapper">
              {filteredRetailers.length !== 0 ? (
                <Table
                  sx={{minWidth: 650}}
                  aria-label="simple table"
                  className="user-table admin-retailer"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">
                        <TableSortLabel
                          active={orderBy === 'name'}
                          direction={orderBy === 'name' ? order : 'asc'}
                          onClick={() => handleRequestSort('name')}
                        >
                          <span className="text">{t('common-name')}</span>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-country')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-city')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-number-sale-people')}</span>
                      </TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(filteredRetailers).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="left">
                          <span className="text">{row.name}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">
                            {t(`countries-${row.area_code}`)}
                          </span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.city}</span>
                        </TableCell>
                        <TableCell align="left">
                          <span className="text">{row.num_salespersons}</span>
                        </TableCell>
                        <TableCell align="right">
                          <RowActionsMenu
                            actions={[
                              {
                                key: `retailer-${row.id}`,
                                label: t('common-retailer'),
                                onClick: () => navigate(`/admin/retailer-info/${row.id}`),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="no-result">
                  <h4 className="txt-center">{t('admin-main-title-empty')}</h4>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('admin-main-tab3-title-empty')}
              <small>{t('admin-retailer-tab3-text-empty')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableRetailer;
